import React, { useContext } from 'react';
import { buildMediaUrl } from '../../../utils/mediaUrl';
import { PreviewContext } from '../PreviewContext';

const AboutPreview = ({ isAdminPreview = true }) => {
  const { liveAboutData } = useContext(PreviewContext);
  const about = liveAboutData;

  if (!about || (!about.title && !about.content)) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
        Add content to see preview
      </div>
    );
  }

  const template = about.layout_variant || 'classic';

  const getImageUrl = (img) => {
    if (!img) return null;
    if (typeof img === 'string') {
      return img.startsWith('blob:') || img.startsWith('http') ? img : buildMediaUrl(img);
    }
    return URL.createObjectURL(img);
  };

  const bgColor = about.background_color || '#0f172a';
  const textColor = about.text_color || '#ffffff';
  const isGradient = bgColor.includes('gradient');
  const bgStyle = isGradient ? { background: bgColor } : { backgroundColor: bgColor };

  // Hero Banner Template
  if (template === 'hero') {
    return (
      <div style={{ ...bgStyle, minHeight: '100%' }}>
        {/* Hero Banner */}
        {about.hero_image && (
          <div style={{
            position: 'relative',
            height: isAdminPreview ? '200px' : '400px',
            overflow: 'hidden',
          }}>
            <img
              src={getImageUrl(about.hero_image)}
              alt="Hero"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, rgba(15,23,42,0.3), rgba(15,23,42,0.9))',
            }} />
            <div style={{
              position: 'absolute',
              bottom: '1.5rem',
              left: '1.5rem',
              right: '1.5rem',
            }}>
              <h1 style={{
                fontSize: isAdminPreview ? '1.5rem' : '2.5rem',
                fontWeight: '700',
                color: textColor,
              }}>
                {about.title || 'About Us'}
              </h1>
              {about.subtitle && (
                <p style={{ color: textColor, opacity: 0.8, marginTop: '0.5rem' }}>
                  {about.subtitle}
                </p>
              )}
            </div>
          </div>
        )}

        <div style={{ padding: '1.5rem' }}>
          {/* Founder Section */}
          {(about.founder_name || about.founder_image) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '0.75rem',
            }}>
              {about.founder_image && (
                <img
                  src={getImageUrl(about.founder_image)}
                  alt={about.founder_name}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              )}
              <div>
                <h3 style={{ color: textColor, fontWeight: '600', fontSize: '1rem' }}>
                  {about.founder_name}
                </h3>
                {about.founder_title && (
                  <p style={{ color: '#6366f1', fontSize: '0.85rem' }}>
                    {about.founder_title}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          {about.content && (
            <p style={{
              color: textColor,
              opacity: 0.8,
              fontSize: '0.9rem',
              lineHeight: '1.7',
              marginBottom: '1.5rem',
            }}>
              {about.content}
            </p>
          )}

          {/* Mission & Vision */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            {about.mission && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '0.5rem',
                borderLeft: '3px solid #6366f1',
              }}>
                <h4 style={{ color: '#6366f1', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                  Mission
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.8rem', lineHeight: '1.5' }}>
                  {about.mission}
                </p>
              </div>
            )}
            {about.vision && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '0.5rem',
                borderLeft: '3px solid #8b5cf6',
              }}>
                <h4 style={{ color: '#8b5cf6', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                  Vision
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.8rem', lineHeight: '1.5' }}>
                  {about.vision}
                </p>
              </div>
            )}
          </div>

          {/* CTA Button */}
          {about.cta_text && (
            <button style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: '600',
              cursor: 'pointer',
            }}>
              {about.cta_text}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Story Template
  if (template === 'story') {
    return (
      <div style={{ ...bgStyle, minHeight: '100%', padding: '1.5rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: isAdminPreview ? '1.5rem' : '2rem',
            fontWeight: '700',
            color: textColor,
            marginBottom: '0.5rem',
          }}>
            {about.title || 'Our Story'}
          </h2>
          {about.subtitle && (
            <p style={{ color: textColor, opacity: 0.6, fontSize: '0.95rem' }}>
              {about.subtitle}
            </p>
          )}
        </div>

        {/* Hero Image */}
        {about.hero_image && (
          <div style={{
            borderRadius: '1rem',
            overflow: 'hidden',
            marginBottom: '1.5rem',
          }}>
            <img
              src={getImageUrl(about.hero_image)}
              alt="Story"
              style={{ width: '100%', height: 'auto' }}
            />
          </div>
        )}

        {/* Main Content */}
        {about.content && (
          <p style={{
            color: textColor,
            opacity: 0.8,
            fontSize: '0.9rem',
            lineHeight: '1.8',
            marginBottom: '2rem',
          }}>
            {about.content}
          </p>
        )}

        {/* Story Section */}
        {about.story_title && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderRadius: '1rem',
            borderLeft: '4px solid #6366f1',
            marginBottom: '1.5rem',
          }}>
            <h3 style={{
              color: textColor,
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '0.75rem',
            }}>
              {about.story_title}
            </h3>
            {about.story_content && (
              <p style={{
                color: textColor,
                opacity: 0.7,
                fontSize: '0.9rem',
                lineHeight: '1.7',
              }}>
                {about.story_content}
              </p>
            )}
          </div>
        )}

        {/* Mission & Vision */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {about.mission && (
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              borderRadius: '0.5rem',
              borderLeft: '3px solid #6366f1',
            }}>
              <h4 style={{ color: '#6366f1', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                Mission
              </h4>
              <p style={{ color: textColor, opacity: 0.7, fontSize: '0.8rem', lineHeight: '1.5' }}>
                {about.mission}
              </p>
            </div>
          )}
          {about.vision && (
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '0.5rem',
              borderLeft: '3px solid #8b5cf6',
            }}>
              <h4 style={{ color: '#8b5cf6', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                Vision
              </h4>
              <p style={{ color: textColor, opacity: 0.7, fontSize: '0.8rem', lineHeight: '1.5' }}>
                {about.vision}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Classic Template (default)
  return (
    <div style={{
      padding: '1.5rem',
      ...bgStyle,
      minHeight: '100%',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: isAdminPreview ? '1.5rem' : '2rem',
          fontWeight: '700',
          color: textColor,
          marginBottom: '0.5rem',
        }}>
          {about.title || 'About Us'}
        </h2>
        {about.subtitle && (
          <p style={{ color: textColor, opacity: 0.6, fontSize: '0.95rem' }}>
            {about.subtitle}
          </p>
        )}
      </div>

      {/* Content with Image */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: about.image ? '1fr 1fr' : '1fr',
        gap: '1.5rem',
        alignItems: 'start',
      }}>
        <div>
          {about.content && (
            <p style={{
              color: textColor,
              opacity: 0.8,
              fontSize: '0.9rem',
              lineHeight: '1.7',
              marginBottom: '1.5rem',
            }}>
              {about.content}
            </p>
          )}

          {/* Mission & Vision */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {about.mission && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '0.5rem',
                borderLeft: '3px solid #6366f1',
              }}>
                <h4 style={{ color: '#6366f1', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                  Mission
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.8rem', lineHeight: '1.5' }}>
                  {about.mission}
                </p>
              </div>
            )}
            {about.vision && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '0.5rem',
                borderLeft: '3px solid #8b5cf6',
              }}>
                <h4 style={{ color: '#8b5cf6', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                  Vision
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.8rem', lineHeight: '1.5' }}>
                  {about.vision}
                </p>
              </div>
            )}
          </div>
        </div>

        {about.image && (
          <div style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>
            <img
              src={getImageUrl(about.image)}
              alt={about.title}
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutPreview;
